import { Card, Typography } from '@mui/material';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SellerTagType, SellerTag } from '@/lib/types/feature/seller-tag';

interface TagPanelProps {
  tagType: SellerTagType;
  onAddValues: () => void;
  onRenameValue: (value: SellerTag) => void;
  onDeleteValue: (value: SellerTag) => void;
}

/**
 * 選択中のタグ種別が持つタグ名の一覧（画面右・タグ種別の下）。
 *
 * ここはタグ名のマスタを扱う場所で、店舗の絞り込みは一覧側の列フィルタで行う。
 */
export function TagPanel({ tagType, onAddValues, onRenameValue, onDeleteValue }: TagPanelProps) {
  const totalSellers = tagType.tags.reduce((sum, v) => sum + v.seller_count, 0);

  return (
    <Card variant="outlined">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
        <Typography variant="subtitle2" fontWeight={700} noWrap title={tagType.name}>
          「{tagType.name}」のタグ名
        </Typography>
        <Button type="button" variant="ghost" size="sm" onClick={onAddValues}>
          <Plus className="mr-1 h-4 w-4" />
          追加
        </Button>
      </div>

      {tagType.tags.length === 0 ? (
        <div className="px-4 py-3">
          <Typography variant="body2" color="text.secondary">
            タグ名がまだありません。「追加」から登録してください。
          </Typography>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {tagType.tags.map((value) => (
            <div
              key={value.id}
              className="flex items-center justify-between gap-2 border-b px-4 py-1.5 last:border-b-0 hover:bg-gray-50"
            >
              <span className="truncate text-sm" title={value.name}>
                {value.name}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <span className="text-xs tabular-nums text-gray-500">{value.seller_count}店</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${value.name} の操作`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRenameValue(value)}>
                      名前を変更
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDeleteValue(value)} className="text-red-600">
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="border-t px-4 py-2 text-xs text-gray-500">
        {tagType.tags.length}件 / {totalSellers}店に付与
      </div>
    </Card>
  );
}
